/**
 * Telemetry — getStats() sampler for active WebRTC calls.
 *
 * - Polls RTCPeerConnection.getStats() every 5s per active call
 * - Buffers samples, flushes batches to `call_stats_samples` every 2s
 * - On stopSampling: aggregates p50/p95 packet loss + avg jitter/rtt
 *   and writes them back to `calls.quality_score_*` columns
 * - Emits `quality_sample` events for UI consumers (NetworkQualityIndicator)
 */

import { supabase } from '../supabase';
import { emit } from './events';
import type { CallId, ClientCallId } from './types';

export interface QualitySample {
  callId: CallId | null;
  clientCallId: ClientCallId;
  sampledAt: Date;
  packetsLost: number | null;
  packetsReceived: number | null;
  jitterMs: number | null;
  rttMs: number | null;
  audioLevel: number | null;
  bytesReceived: number | null;
  /**
   * Uitgaande kant (wat de klant hoort). Tot 22-10-2026 maten we alleen de
   * inkomende stroom; "ze horen me ver weg en haperend" was daardoor niet
   * aantoonbaar. remote-inbound-rtp is Telnyx' rapport over ónze stroom,
   * media-source het microfoonniveau vóór codering, de kandidaat zegt of de
   * media direct (host/srflx) of via een TURN-relay loopt.
   */
  packetsSent: number | null;
  bytesSent: number | null;
  remotePacketsLost: number | null;
  remoteJitterMs: number | null;
  remoteFractionLost: number | null;
  micAudioLevel: number | null;
  localCandidateType: string | null;
  candidateRttMs: number | null;
}

interface Sampler {
  interval: ReturnType<typeof setInterval>;
  buffer: QualitySample[];
}

const SAMPLE_INTERVAL_MS = 5000;
const FLUSH_DELAY_MS = 2000;
const FLUSH_BATCH_SIZE = 100;

const samplers = new Map<ClientCallId, Sampler>();
const flushQueue: QualitySample[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

export function startSampling(clientCallId: ClientCallId, telnyxCall: unknown): void {
  if (samplers.has(clientCallId)) return;

  const buffer: QualitySample[] = [];

  const interval = setInterval(async () => {
    try {
      const pc = pickPeerConnection(telnyxCall);
      if (!pc) return;

      const stats = await pc.getStats();
      const inbound = findInboundRtp(stats);
      const remoteInbound = findRemoteInboundRtp(stats);
      if (!inbound) return;
      const outbound = findOutboundRtp(stats);
      const mediaSource = findMediaSource(stats, outbound?.mediaSourceId);
      const pair = findSelectedCandidatePair(stats);
      const localCandidate = pair ? stats.get(pair.localCandidateId) : null;

      const sample: QualitySample = {
        callId: null,
        clientCallId,
        sampledAt: new Date(),
        packetsLost: toInt(inbound.packetsLost),
        packetsReceived: toInt(inbound.packetsReceived),
        jitterMs: inbound.jitter != null ? Number((inbound.jitter * 1000).toFixed(3)) : null,
        rttMs: remoteInbound?.roundTripTime != null
          ? Number((remoteInbound.roundTripTime * 1000).toFixed(3))
          : null,
        audioLevel: inbound.audioLevel ?? null,
        bytesReceived: toInt(inbound.bytesReceived),
        packetsSent: toInt(outbound?.packetsSent),
        bytesSent: toInt(outbound?.bytesSent),
        remotePacketsLost: toInt(remoteInbound?.packetsLost),
        remoteJitterMs:
          remoteInbound?.jitter != null ? Number((remoteInbound.jitter * 1000).toFixed(3)) : null,
        remoteFractionLost:
          typeof remoteInbound?.fractionLost === 'number'
            ? Number(remoteInbound.fractionLost.toFixed(4))
            : null,
        micAudioLevel:
          typeof mediaSource?.audioLevel === 'number'
            ? Number(mediaSource.audioLevel.toFixed(4))
            : null,
        localCandidateType: (localCandidate?.candidateType as string | undefined) ?? null,
        candidateRttMs:
          typeof pair?.currentRoundTripTime === 'number'
            ? Number((pair.currentRoundTripTime * 1000).toFixed(3))
            : null,
      };

      buffer.push(sample);
      flushQueue.push(sample);
      scheduleFlush();

      emit({
        type: 'quality_sample',
        source: 'client',
        clientCallId,
        metadata: { sample },
      });
    } catch (err) {
      console.warn('[telephony/telemetry] getStats failed', err);
    }
  }, SAMPLE_INTERVAL_MS);

  samplers.set(clientCallId, { interval, buffer });
}

export async function stopSampling(
  clientCallId: ClientCallId,
  callId: CallId | null,
): Promise<void> {
  const sampler = samplers.get(clientCallId);
  if (!sampler) return;

  clearInterval(sampler.interval);
  samplers.delete(clientCallId);

  // Resolve pending samples with the now-known callId before the final flush.
  if (callId) {
    flushQueue.forEach((s) => {
      if (s.clientCallId === clientCallId && s.callId === null) {
        s.callId = callId;
      }
    });
  }
  await flush();

  if (!callId || sampler.buffer.length === 0) return;

  const losses = sampler.buffer
    .map((s) => s.packetsLost ?? 0)
    .filter((n) => n >= 0)
    .sort((a, b) => a - b);
  const jitters = sampler.buffer
    .map((s) => s.jitterMs ?? 0)
    .filter((n) => n > 0)
    .sort((a, b) => a - b);
  const rtts = sampler.buffer
    .map((s) => s.rttMs ?? 0)
    .filter((n) => n > 0)
    .sort((a, b) => a - b);

  const totalReceived = sampler.buffer.reduce((acc, s) => acc + (s.packetsReceived ?? 0), 0);
  const totalLost = sampler.buffer.reduce((acc, s) => acc + (s.packetsLost ?? 0), 0);
  const denom = totalReceived + totalLost;
  const lossPct = denom > 0 ? (totalLost / denom) * 100 : 0;

  // Uitgaande samenvatting (één event per gesprek, geen migratie nodig):
  // packetsSent/remotePacketsLost zijn cumulatief, dus het laatste sample
  // telt; microfoonniveau als gemiddelde en maximum over het gesprek.
  const last = sampler.buffer[sampler.buffer.length - 1]!;
  const micLevels = sampler.buffer
    .map((s) => s.micAudioLevel)
    .filter((v): v is number => typeof v === 'number');
  const remoteJitters = sampler.buffer
    .map((s) => s.remoteJitterMs)
    .filter((v): v is number => typeof v === 'number' && v > 0);
  const sent = last.packetsSent ?? 0;
  const remoteLost = last.remotePacketsLost ?? 0;
  const outboundLossPct = sent + remoteLost > 0 ? (remoteLost / (sent + remoteLost)) * 100 : null;
  emit({
    type: 'quality_summary',
    source: 'client',
    clientCallId,
    callId,
    metadata: {
      samples: sampler.buffer.length,
      inboundLossPct: Number(lossPct.toFixed(2)),
      outboundLossPct: outboundLossPct === null ? null : Number(outboundLossPct.toFixed(2)),
      packetsSent: last.packetsSent,
      remotePacketsLost: last.remotePacketsLost,
      avgRemoteJitterMs: avg(remoteJitters),
      avgJitterMs: avg(jitters),
      avgRttMs: avg(rtts),
      avgMicLevel: avg(micLevels),
      maxMicLevel: micLevels.length ? Number(Math.max(...micLevels).toFixed(4)) : null,
      localCandidateType: last.localCandidateType,
    },
  });

  try {
    await supabase
      .from('calls')
      .update({
        packet_loss_pct: Number(lossPct.toFixed(2)),
        avg_jitter_ms: avg(jitters),
        avg_rtt_ms: avg(rtts),
        quality_score_p50: percentile(losses, 0.5),
        quality_score_p95: percentile(losses, 0.95),
      })
      .eq('id', callId);
  } catch (err) {
    console.warn('[telephony/telemetry] aggregate write failed', err);
  }
}

// ─── Flushing ──────────────────────────────────────────────────────

function scheduleFlush(): void {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flush();
  }, FLUSH_DELAY_MS);
}

async function flush(): Promise<void> {
  if (flushQueue.length === 0) return;

  const batch = flushQueue.splice(0, FLUSH_BATCH_SIZE);
  try {
    const { error } = await supabase.from('call_stats_samples').insert(
      batch.map((s) => ({
        call_id: s.callId,
        client_call_id: s.clientCallId,
        sampled_at: s.sampledAt.toISOString(),
        packets_lost: s.packetsLost,
        packets_received: s.packetsReceived,
        jitter_ms: s.jitterMs,
        rtt_ms: s.rttMs,
        audio_level: s.audioLevel,
        bytes_received: s.bytesReceived,
        metadata: {
          packetsSent: s.packetsSent,
          bytesSent: s.bytesSent,
          remotePacketsLost: s.remotePacketsLost,
          remoteJitterMs: s.remoteJitterMs,
          remoteFractionLost: s.remoteFractionLost,
          micAudioLevel: s.micAudioLevel,
          localCandidateType: s.localCandidateType,
          candidateRttMs: s.candidateRttMs,
        },
      })),
    );
    if (error) throw error;
  } catch (err) {
    console.warn('[telephony/telemetry] flush failed', err);
  }

  // Drain remainder in the next tick if any.
  if (flushQueue.length > 0) scheduleFlush();
}

// ─── Helpers ───────────────────────────────────────────────────────

function pickPeerConnection(telnyxCall: unknown): RTCPeerConnection | null {
  const c = telnyxCall as { peer?: { instance?: unknown } } | null;
  const pc = c?.peer?.instance;
  if (pc && typeof (pc as RTCPeerConnection).getStats === 'function') {
    return pc as RTCPeerConnection;
  }
  return null;
}

function findInboundRtp(stats: RTCStatsReport): any {
  for (const r of stats.values()) {
    if (r.type === 'inbound-rtp' && r.kind === 'audio') return r;
  }
  return null;
}

function findRemoteInboundRtp(stats: RTCStatsReport): any {
  for (const r of stats.values()) {
    if (r.type === 'remote-inbound-rtp' && r.kind === 'audio') return r;
  }
  return null;
}

function findOutboundRtp(stats: RTCStatsReport): any {
  for (const r of stats.values()) {
    if (r.type === 'outbound-rtp' && r.kind === 'audio') return r;
  }
  return null;
}

function findMediaSource(stats: RTCStatsReport, mediaSourceId: string | undefined): any {
  if (mediaSourceId) {
    const direct = stats.get(mediaSourceId);
    if (direct) return direct;
  }
  for (const r of stats.values()) {
    if (r.type === 'media-source' && r.kind === 'audio') return r;
  }
  return null;
}

/** Het actieve kandidaatpaar: via transport.selectedCandidatePairId, anders het genomineerde succes-paar. */
function findSelectedCandidatePair(stats: RTCStatsReport): any {
  for (const r of stats.values()) {
    if (r.type === 'transport' && r.selectedCandidatePairId) {
      const pair = stats.get(r.selectedCandidatePairId);
      if (pair) return pair;
    }
  }
  for (const r of stats.values()) {
    if (r.type === 'candidate-pair' && r.nominated && r.state === 'succeeded') return r;
  }
  return null;
}

function toInt(v: unknown): number | null {
  if (typeof v !== 'number' || !Number.isFinite(v)) return null;
  return Math.trunc(v);
}

function avg(arr: number[]): number | null {
  if (arr.length === 0) return null;
  return Number((arr.reduce((a, b) => a + b, 0) / arr.length).toFixed(3));
}

function percentile(arr: number[], p: number): number | null {
  if (arr.length === 0) return null;
  const idx = Math.min(arr.length - 1, Math.floor(arr.length * p));
  const v = arr[idx];
  return typeof v === 'number' ? Number(v.toFixed(2)) : null;
}
